<script setup lang="ts">
import AlipayLogo from '@/assets/payment/alipay_colored.svg'
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { usePaymentPlan } from '@/composables/paymentPlan'
import { useFetch } from '@/composables/fetch'
import {
  AdyenCheckout,
  WeChat,
  Redirect,
  type CoreConfiguration,
  type PaymentFailedData,
  type PaymentCompletedData,
} from '@adyen/adyen-web'
import { useRouter } from 'vue-router'
import { t } from '@/locales/i18n'
import { updateSubscription, segmentTrackSuccess } from '@/helpers/paymentHelpers'
import type { PaymentPlan } from '@/types/paymentPlans'
import PaymentLoader from '@/components/Payments/PaymentLoader.vue'
import AdyenPaymentErrors from '@/components/Payments/AdyenPaymentErrors.vue'
import WechatLogo from '@/assets/payment/wechat-pay_colored.svg'
import PaymentMethodCard from '@/components/Payments/PaymentMethodCard.vue'
import { logSentry } from '@gto/sentry'
import { adyenConfirmResult, useAdyenCreditsPayment } from '@gto/api/vue-query'

const { selectedPlan, selectedTierPlan, currentPlan, currentSubscription, currentTierPlan } =
  usePaymentPlan()
const { fetch } = useFetch()
const router = useRouter()
const store = useStore()

// Props

interface OtherGatewayProps {
  onSuccessPayment: (params: { payment_provider: string; payment_method: string }) => void
  singlePayment?: boolean
  customPlan?: PaymentPlan
}

const props = defineProps<OtherGatewayProps>()
const emit = defineEmits(['payment-done'])

interface AdyenError {
  error: boolean
  message?: string
  status?: string
}
interface FormCreationErrorParams {
  error: object | string
  endpoint?: string
}

const formCreationError = ref<FormCreationErrorParams | null>(null)
const localLoading = ref(false)
const updatingSubscription = ref(false)
const updateSubscriptionTimedOut = ref(false)
const formLoading = ref(true)
const redirecting = ref(false)
const { alipayEnabled, wechatPayEnabled } = usePaymentPlan()

const setupAlipayPaymentButtonClickListener = () => {
  const alipayButton = document.querySelector('#alipay-container .adyen-checkout__button')

  if (alipayButton) {
    alipayButton.addEventListener('click', () => {
      redirecting.value = true
    })
  }
}

const isPendingPayment = computed(() => {
  const { pending_payment } = router.currentRoute.value.query
  return pending_payment === 'true'
})

const adyenError = ref<AdyenError>({
  error: false,
})

const productionENV =
  import.meta.env.VITE_ENV === 'production' && import.meta.env.VITE_BRANCH === 'master'

const handlePaymentFailure = async (error: AdyenError) => {
  await store.dispatch('globals_action_reset')
  localLoading.value = false
  adyenError.value = error
  router.replace({
    query: { ...router.currentRoute.value.query, pending_payment: undefined },
  })
  logSentry('Other Gateway: Payment failed', {
    error: JSON.stringify(error, null, 2),
    email: store.getters.user?.email,
    selectedPlan: selectedTierPlan.value?.full_name,
    currentPlan: currentTierPlan.value?.full_name,
  })
  resetAdyenSession()
}

const handlePaymentSuccess = async (result: PaymentCompletedData, plan: PaymentPlan) => {
  if (result.resultCode !== 'Authorised') {
    const message = result.resultCode === 'Refused' ? t('payment.error.payment_was_refused') : ''
    handlePaymentFailure({ error: true, message, status: 'UNKNOWN_ERROR' })
    return
  }

  const { sessionId } = router.currentRoute.value.query
  if ('sessionResult' in result && typeof sessionId === 'string') {
    // This request is needed for immediate confirmation of the payment. Webhook may take a while.
    adyenConfirmResult({ session_id: sessionId, session_result: result.sessionResult })
    router.replace({
      query: {
        ...router.currentRoute.value.query,
        sessionId: undefined,
      },
    })
  }

  emit('payment-done')
  updatingSubscription.value = true
  updateSubscriptionTimedOut.value = false
  localStorage.setItem('gtm_payment_session', Date.now().toString())
  updateSubscription(
    plan,
    'upgrade',
    () => {
      updatingSubscription.value = false
      router.replace({
        query: { ...router.currentRoute.value.query, pending_payment: undefined },
      })
      segmentTrackSuccess(selectedTierPlan.value, 'alipay')

      props.onSuccessPayment({
        payment_provider: 'adyen',
        payment_method: 'alipay',
      })
    },
    () => {
      updateSubscriptionTimedOut.value = true
      router.replace({
        query: { ...router.currentRoute.value.query, pending_payment: undefined },
      })
    },
  )
}

const createError = (error: object, endpoint: string) => {
  formCreationError.value = {
    error: Object.keys(error).length ? error : t('payment.errors.create_form'),
    endpoint,
  }
  logSentry('Other Gateway: Form creation error', {
    error,
    email: store.getters.user?.email,
    selectedPlan: selectedTierPlan.value?.full_name,
    currentPlan: currentTierPlan.value?.full_name,
  })
}

const createAdyenConfig = (
  plan: PaymentPlan,
  sessionId: string,
  sessionData?: string,
): CoreConfiguration => ({
  locale: 'en_US',
  countryCode: 'US',
  environment: productionENV ? 'live' : 'test',
  clientKey: import.meta.env.VITE_ADYEN_WEB_KEY,
  analytics: { enabled: false },
  translations: {
    en_US: {
      continueTo: 'Continue to',
    },
  },
  session: {
    id: sessionId,
    sessionData: sessionData || '',
  },
  amount: {
    value: selectedPlan.value?.price || 0,
    currency: 'USD',
  },
  onPaymentCompleted: (result: PaymentCompletedData) => handlePaymentSuccess(result, plan),
  onPaymentFailed: (err: PaymentFailedData) => {
    const message = err.resultCode === 'Refused' ? t('payment.error.payment_was_refused') : ''
    handlePaymentFailure({ error: true, message, status: 'UNKNOWN_ERROR' })
  },
  onError: (err: any) => {
    handlePaymentFailure({ error: true, message: err.message, status: 'UNKNOWN_ERROR' })
  },
})

const createPaymentSession = async (plan: PaymentPlan, paymentMethod: 'ALIPAY' | 'WECHAT') => {
  const upgradeSub =
    currentPlan.value && currentPlan.value?.tier !== 'FREE' && currentPlan.value?.tier !== 'TRIAL'
  const endpoint = `payments/subscription/adyen/${upgradeSub ? `${currentSubscription.value!.public_id}/` : ''}`
  const { data, error } = await fetch({
    conf: {
      endpoint,
      apiVersion: 'v1',
      method: upgradeSub ? 'patch' : 'post',
    },
    data: {
      plan: plan.id,
      use_card: false,
      country_code: 'US',
      return_url: `${router.currentRoute.value.path}?${new URLSearchParams({
        ...router.currentRoute.value.query,
        pending_payment: 'true',
      })}`,
      payment_method: paymentMethod,
    },
  })

  if (error || !data?.ext_id || !data?.session_data) {
    createError(error, endpoint)
    return null
  }

  const { ext_id, session_data } = data

  return { ext_id, session_data }
}

const {
  mutateAsync: mutateCreateCreditsPayment,
  data: createCreditsPaymentData,
  error: createCreditsPaymentError,
} = useAdyenCreditsPayment()

async function createSinglePaymentSession(plan: PaymentPlan) {
  await mutateCreateCreditsPayment({
    data: {
      credit_amount: plan.credits,
      country_code: 'US',
      return_url: `${window.location.origin}/user/credits`,
      use_card: false,
    },
  })
  if (!createCreditsPaymentData.value) {
    createError(createCreditsPaymentError, 'payments/adyen/credits/')
    return null
  }

  // Check if the response is a session response (has ext_id and session_data)
  if (
    'ext_id' in createCreditsPaymentData.value &&
    'session_data' in createCreditsPaymentData.value
  ) {
    const { ext_id, session_data } = createCreditsPaymentData.value
    if (!ext_id || !session_data) {
      createError(createCreditsPaymentError, 'payments/adyen/credits/')
      return null
    }
    return { ext_id, session_data }
  }
  // Handle transaction response case
  createError(createCreditsPaymentError, 'payments/adyen/credits/')
  return null
}

const initializeCheckout = async (plan: PaymentPlan, sessionId: string, sessionData?: string) => {
  const config = createAdyenConfig(plan, sessionId, sessionData)
  return AdyenCheckout(config)
}

async function initialize() {
  const plan = props.singlePayment ? props.customPlan : selectedPlan.value

  if (!plan?.id) {
    formCreationError.value = { error: 'payment.error.no_plan_selected' }
    return
  }

  const { sessionId, redirectResult } = router.currentRoute.value.query
  if (
    isPendingPayment.value &&
    typeof sessionId === 'string' &&
    typeof redirectResult === 'string'
  ) {
    try {
      const checkout = await initializeCheckout(plan, sessionId)
      checkout.submitDetails({ details: { redirectResult } })
      router.replace({
        query: {
          ...router.currentRoute.value.query,
          redirectResult: undefined,
        },
      })
    } catch (err) {
      formLoading.value = false
      router.replace({
        query: {
          ...router.currentRoute.value.query,
          pending_payment: undefined,
          redirectResult: undefined,
          sessionId: undefined,
        },
      })
    }
    return
  }

  if (alipayEnabled.value) {
    const alipaySession = props.singlePayment
      ? await createSinglePaymentSession(plan)
      : await createPaymentSession(plan, 'ALIPAY')
    if (alipaySession) {
      const checkout = await initializeCheckout(
        plan,
        alipaySession.ext_id,
        alipaySession.session_data,
      )
      new Redirect(checkout, {
        type: 'alipay',
      }).mount('#alipay-container')
    }
  }

  if (wechatPayEnabled.value) {
    const wechatSession = props.singlePayment
      ? await createSinglePaymentSession(plan)
      : await createPaymentSession(plan, 'WECHAT')
    if (wechatSession) {
      const checkout = await initializeCheckout(
        plan,
        wechatSession.ext_id,
        wechatSession.session_data,
      )
      new WeChat(checkout).mount('#wechatpayWeb-container')
    }
  }
  formLoading.value = false

  setupAlipayPaymentButtonClickListener()
}

function resetAdyenSession() {
  formLoading.value = true
  initialize()
}
onMounted(() => {
  initialize()
})
</script>

<template>
  <div class="adyen-box anim-fade-in">
    <AdyenPaymentErrors
      :form-creation-error="formCreationError"
      :adyen-error="adyenError"
    />
    <PaymentLoader
      :is-loading="formLoading || redirecting"
      :is-updating="updatingSubscription || isPendingPayment"
      :is-timed-out="updateSubscriptionTimedOut"
      :selected-plan="selectedPlan"
      :form-creation-error="Boolean(formCreationError)"
    />
    <section
      class="payment-methods"
      :class="{ hidden: formLoading || updatingSubscription || isPendingPayment || redirecting }"
    >
      <PaymentMethodCard
        v-if="alipayEnabled"
        title="Alipay"
        :logo="AlipayLogo"
      >
        <div id="alipay-container"></div>
      </PaymentMethodCard>
      <PaymentMethodCard
        v-if="wechatPayEnabled"
        title="WeChat Pay"
        :logo="WechatLogo"
      >
        <div id="wechatpayWeb-container"></div>
      </PaymentMethodCard>
      <slot
        name="text"
        :tierPlan="selectedTierPlan"
      />
    </section>
  </div>
</template>

<style lang="scss">
@import '@adyen/adyen-web/styles/adyen.css';

.payment-methods {
  display: flex;
  column-gap: 1rem;
  flex-wrap: wrap;

  &.hidden {
    opacity: 0;
    height: 0;
    pointer-events: none;
  }
}

.adyen-checkout__button {
  border-radius: 3.5px;
  height: 44px;
  text-transform: uppercase;
  font-size: 1rem;
  letter-spacing: 1.2px;
}

#alipay-container .adyen-checkout__button {
  background: rgb(44, 164, 221);

  &:hover {
    background: rgb(35, 131, 175);
  }

  .adyen-checkout__button__text {
    visibility: hidden;
    position: relative;

    &::after {
      content: 'Continue';
      visibility: visible;
      position: absolute;
      left: 0;
      right: 0;
      text-align: center;
    }
  }
}

#wechatpayWeb-container .adyen-checkout__button {
  background: #16aa16;

  &:hover {
    background: #118711;
  }

  .adyen-checkout__button__text {
    visibility: hidden;
    position: relative;

    &::after {
      content: 'Continue';
      visibility: visible;
      position: absolute;
      left: 0;
      right: 0;
      text-align: center;
    }
  }
}
</style>
